<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <export-sidebar
          sidebar-id="sidebar-customers-export"
          storage-key="sidebar_customers_export"
          :columns="exportableFields"
          :data-to-export="dataToExport"
          :data-mapper="mapCustomerForExport"
          file-name="Customers.xlsx"
          sheet-name="Customers"
          :is-busy="isBusy"
          :items-count="totalRows"
        />
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-button v-if="canEditCustomers()" class="custom-btn-primary" :disabled="isBusy" @click="showModal(null, false)">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый заказчик
        </b-button>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col md="6" cols="12">
            <label class="small text-muted">Города</label>
            <b-form-select
              id="input-town"
              size="sm"
              v-model="filters.town"
            >
              <option :value="-1">Все города</option>
              <option v-for="option in towns" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
          <b-col md="6" cols="12">
            <label class="small text-muted">Тип</label>
            <b-form-select
              id="input-urflag"
              class="custom-radio"
              v-model="filters.urflag"
              :options="urflagOptions"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="customers"
            ref="customers"
            class="custom-table mb-0 text-small"
            sticky-header
            :tbody-tr-class="tbodyRowClass"
            select-mode="multi"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            @row-clicked="onRowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="5"/>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                size="md"
                @change="selectPageRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                class="mx-1"
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowClicked(row.item)"
              />
            </template>

            <template #cell(phone)="row">
              <a :href="telUrl(customerPhone(row.item))" class="text-nowrap" style="color: #1A9167 !important;">
                <v-icon v-if="customerPhone(row.item)" name="buttons/phone" :size="12" class="mr-1"/>
                {{ phoneString(customerPhone(row.item)) }}
              </a>
            </template>

            <template #cell(address)="row">
              <div v-if="row.item.address.length < 50">{{ row.item.address }}</div>
              <div v-else>{{ row.item.address.substring(0, 50) + ".." }}</div>
            </template>

            <template #cell(urflag)="row">
              <div v-if="String(row.item.urflag) === 'true'">
                <v-icon name="buttons/work" :size="15" v-b-tooltip.hover.top="{ title: 'Юр лицо', customClass: 'custom-tooltip' }"/>
              </div>
              <div v-else-if="String(row.item.urflag) === 'false'">
                <v-icon name="buttons/user_check" :size="15" v-b-tooltip.hover.top="{ title: 'Физ лицо', customClass: 'custom-tooltip' }"/>
              </div>
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15" class="text-secondary"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="showModal(row.item.id, true)">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditCustomers()" variant="primary" @click.stop="showModal(row.item.id, false)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item v-if="canEditCustomers()" variant="danger" class="btn-delete" @click.stop="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <span v-if="selected.length > 0" class="text-small mr-2">
                Выбрано: <b>{{selected.length}}</b>
              </span>
              <span v-if="selected.length > 0" class="text-small vertical-divider"/>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных заказчиков.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте нового заказчика прямо сейчас</p>
          <b-btn v-if="canEditCustomers()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="showModal(null, false)">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый заказчик
          </b-btn>
        </div>
      </div>
    </div>

    <customer ref="customer" @refresh="getItems" @modal-closed="onModalClosed"/>

    <dynamic-tables
      storage-key="customers_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="name"
    />
  </div>
</template>

<script>
import customers from "../../network/customers";
import catalogs from "../../network/catalogs";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import Customer from "./Customer";

export default {
  name: 'Customers',
  components: { TablesSkeleton, ExportSidebar, FiltersDrawer, DynamicTables, Customer, PaginationFooter },
  mixins: [
    customers, catalogs,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Заказчики',
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class:'customersSelect'
      },
      fields: [
        {
          key: 'name',
          label: this.$t('message.customers.table.name'),
          class: 'customers-name',
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'inn',
          label: this.$t('message.customers.table.inn'),
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'kpp',
          label: this.$t('message.customers.table.kpp'),
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'ogrn',
          label: this.$t('message.customers.table.ogrn'),
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'phone',
          label: this.$t('message.customers.table.phone'),
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'address',
          label: this.$t('message.customers.table.address'),
          sortable: false,
          visible: true,
          exportable: true,
        },
        {
          key: 'urflag',
          label: this.$t('message.customers.table.urflag'),
          class: 'text-center',
          sortable: false,
          visible: true,
          exportable: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.customers.table.actions'),
        class: 'customers-action'
      },
      towns: [],
      urflagOptions: [
        { value: null, text: 'Показывать все' },
        { value: 'true', text: 'Юр лицо' },
        { value: 'false', text: 'Физ лицо' },
      ],
      sortBy: 'id',
      sortDesc: true,
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        name: '',
      },
      selected: [],
      allSelected: false,
      indeterminate: false,
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) this.filters.query = query.query;

      if (query.town) this.filters.town = query.town;

      if (query.urflag) {
        if (query.urflag === 'true') {
          this.filters.urflag = true;
        } else {
          this.filters.urflag = query.urflag === 'false' ? false : null;
        }
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.towns = (await this.getTowns({})).data.items;
    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        this.serviceStartField,
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.items;
    },
    exportableFields() {
      return this.fields
        .filter(field => field.exportable)
        .map(field => ({
          key: field.key,
          label: field.label,
          visible: field.visible,
        }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.town !== defaults.town) {
        const foundItem = this.towns.find(s => s.id === this.filters.town);
        tags.push({ key: 'town', label: foundItem ? foundItem.name : 'Город' });
      }

      if (this.filters.urflag !== defaults.urflag) {
        const foundItem = this.urflagOptions.find(s => s.value === this.filters.urflag);
        tags.push({ key: 'urflag', label: foundItem ? foundItem.text : 'Тип' });
      }

      return tags;
    },
    isFiltered() {
      const hasQuery = !!this.filters.query && this.filters.query.trim().length > 0;
      const hasTags = this.activeFilterTags.length > 0;

      return hasQuery || hasTags;
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.customer) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.customer.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.customer) {
            this.$refs.customer.hideModal();
          }
        });
      }, immediate: true
    },
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.items.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        town: this.filters.town,
        urflag: this.filters.urflag,
      }

      this.isBusy = true;
      const result = await this.getCustomers(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.updateSelected();
    },
    showModal(customerId, isViewonly) {
      const canEdit = this.canEditCustomers();

      if (!customerId) {
        if (!canEdit) return;

        this.$refs.customer.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'Customers',
        params: { id: customerId },
        query: query
      });
    },
    onRowDblClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'Customers',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    onRemove(row) {
      if (!this.canEditCustomers()) return;

      if (row.item) {
        this.deleteRow = row.item
      }

      const title = "Удаление заказчика";
      const message = `Вы действительно хотите удалить заказчика "${this.deleteRow.name}"?`;
      this.showModalDelete(title, message, async () => {

        const result = await this.deleteCustomer(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заказчик удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    urFlagString(flag) {
      return flag ? "Юр лицо" : "Физ лицо";
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        town: -1,
        urflag: null
      }
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'town') {
        this.filters.town = -1;
      }
      if (tag.key === 'urflag') {
        this.filters.urflag = null;
      }
      this.onSearch();
    },
    customerPhone(customer) {
      return customer.contacts && customer.contacts.length > 0 ? customer.contacts[0].phone : '';
    },
    // Export
    mapCustomerForExport(item) {
      return {
        name: item.name,
        inn: item.inn,
        kpp: item.kpp,
        ogrn: item.ogrn,
        phone: this.customerPhone(item),
        address: item.address,
        urflag: item.urflag ? 'Юр лицо' : 'Физ лицо',
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.items] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Permissions
    canEditCustomers() {
      return this.hasPermission([PERM.CUSTOMER_EDIT]);
    },
  },
}
</script>

<style>
.customers-name {
  min-width: 200px;
  max-width: 300px;
}

.customers-action {
  width: 80px;
  text-align: center;
}

#customers td {
  vertical-align: middle !important;
}

.customersSelect {
  min-width: 50px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Customers/Customers.vue